<template>
  <VSpinner v-if="!oldProduct" class="offset-top-16 offset-bottom-17" />
  <article v-else>
    <div id="PRODUCT_CHANGE_SUMMARY">
      <div class="container-fixed">
        <div class="row">
          <div class="col-l-12">
            <!-- #53, #56 -->
            <VProgress v-if="flags.progressBarEnabled" class="offset-top-2 offset-bottom-2" :steps="steps" active="substore.progress_summary" />
          </div>
        </div>
      </div>

      <section>
        <div class="container-fixed">
          <h3 class="marketing-l">
            <!-- #168 -->
            {{ $t('substore.your_current_product') }}
          </h3>

          <!-- #170, #171, #188 -->
          <VProductChangeDescription :product="oldProduct" property-suffix="Summary" class="offset-top-1 offset-bottom-1" />
        </div>

        <div class="grayed container-fluid">
          <div class="container-fixed">
            <div class="row">
              <div class="col-l-12 offset-top-1 offset-bottom-1">
                <!-- #189 -->
                <VPrice :price="oldProduct.price" :promotion-processing="false" :override-text-regular="oldProduct.texts.regularPriceOverrideText" />
              </div>
            </div>
          </div>
        </div>
      </section>

      <section>
        <div class="container-fixed offset-top-2 offset-bottom-1">
          <h3 class="marketing-l">
            <!-- #199 -->
            {{ $t('substore.switch_to') }}
          </h3>
        </div>

        <div class="container-fixed">
          <!-- #190, #191, #192 -->
          <VProductChangeDescription :product="newProduct" property-suffix="Summary" />
        </div>
      </section>

      <VSection v-if="newProduct.texts.productRequirementsDetails" cluster label="substore.requirements">
        <p v-html="newProduct.texts.productRequirementsDetails"></p>
      </VSection>
    </div>

    <!-- #63-69, #340-345 -->
    <div class="container-fluid grayed offset-top-1 offset-bottom-2">
      <div class="container-fixed">
        <section class="offset-top-1" style="padding-bottom: 6px">
          <VPriceDetails v-bind="newProduct" />
        </section>
      </div>
    </div>

    <!-- #80-89, #160-163 -->
    <div class="container-fixed">
      <VForm
        :hyperlinks="newProduct.hyperlinks"
        :texts="newProduct.texts"
        :text-lists="newProduct.textLists"
        :can-not-succeed="newProduct.canNotSucceed"
        v-bind="flags"
        @submit="onSubmit"
      >
        <!-- 73-->
        <button type="button" class="btn btn-clean offset-right-1" @click="onAbort">
          {{ $t('substore.abort') }}
        </button>

        <span v-if="flags.canNotSucceed">{{ $t('substore.msg_order_currently_not_possible') }}</span>
        <button v-else type="submit" class="btn btn-default btn-brand" :disabled="orderInProgress">
          <template v-if="flags.obligationToPay">
            <!-- TODO: CAP-2042 MagentaCloud -->

            {{ $t('substore.order_btn_obligation') }}
          </template>
          <template v-else>{{ $t('substore.order_btn') }}</template>
        </button>
      </VForm>

      <transition name="slide" appear>
        <div v-if="orderInProgress" class="pull-right offset-top-1 text-emphased">
          <span role="status">{{ $t('substore.hint_text_order_in_progress') }}</span>
          <VSpinner class="offset-top-1" style="margin-right: 0" />
        </div>
      </transition>
    </div>

    <div class="container-fixed offset-top-6">
      <VFootnotes :footnotes="footnotes" />
    </div>
  </article>
</template>

<script>
import { mapActions, mapGetters, mapState } from 'vuex';
import Proxy from '@/proxies/SubstoreStockProxy';
import ChangeActionProxy from '@/proxies/SubstoreChangeActionProxy';
import Transformer from '@/transformers/ProductChangeSummaryTransformer';
import Footnotes from '@/utils/Footnotes';
import TitleMixin from '@/mixins/title';
import OrderStepsMixin from '@/mixins/orderSteps';
import VSpinner from '@/components/Spinner';
import VSection from '@/components/Section';
import emitter from '@/plugins/emitter';
import { ERROR, PRODUCT_CATALOGUE, STOCK, STOCK_PRODUCT_CHANGE_CONFIRMATION, STOCK_PRODUCT_CHANGE_SUMMARY } from '@route-types';

export default {
  name: 'StockProductChangeSummary',

  components: {
    VSpinner,
    VSection,
    VPrice: () => import('@/components/Substore/Price'),
    VProgress: () => import('@/components/Progress'),
    VPriceDetails: () => import('@/components/Substore/PriceDetails'),
    VProductChangeDescription: () => import('@/components/Substore/StockProduct/ProductChangeDescription'),
    VFootnotes: () => import('@/components/UI/Footnotes'),
    VForm: () => import('./Form'),
  },

  mixins: [TitleMixin, OrderStepsMixin],

  props: {
    subscriptionTemplateId: {
      type: String,
      required: true,
    },

    newSubscriptionTemplateId: {
      type: String,
      required: true,
    },

    uniqueSubscriptionId: {
      type: String,
      required: true,
    },

    hideSelection: {
      type: Boolean,
      required: false,
    },
  },

  data() {
    return {
      oldProduct: null,
      newProduct: null,
      footnotes: null,
      form: null,
      orderInProgress: false,
      isOptin: false, // TODO
      flags: {
        progressBarEnabled: null,
        orderSelectionSkipped: null,
        obligationToPay: null,
        customerEmail: null,
        emailVerification: null,
        orderId: null,
        isOnlyReservationAllowed: null,
        isPurchaseRedirectRequired: null,
        thirdPartyAuthentication: null,
        isFinalizationAllowed: null,
        isCompleteOrderAllowed: null,
        subscriptionBookedByExtern: null,
        subscriptionBeginIsRunning: null,
        isFinalization: null,
        isResumingSubscription: null,
      },
    };
  },

  computed: {
    ...mapGetters('system', ['hasAccessCatalogue', 'hasAccessStockOverview']),

    ...mapState('system', ['isDeeplink', 'deeplinkSubscriptionTemplateId', 'deeplinkUniqueSubscriptionId', 'deeplinkNewSubscriptionTemplateId']),
  },

  async created() {
    if (this.isDeeplink && this.deeplinkSubscriptionTemplateId && !this.subscriptionTemplateId) {
      this.$router.replace({
        name: STOCK_PRODUCT_CHANGE_SUMMARY,
        params: {
          subscriptionTemplateId: this.deeplinkSubscriptionTemplateId,
          uniqueSubscriptionId: this.deeplinkUniqueSubscriptionId,
          newSubscriptionTemplateId: this.deeplinkNewSubscriptionTemplateId,
          hideSelection: true,
        },
      });
      return;
    }

    if (this.isDeeplink) {
      this.$store.dispatch('system/resetDeeplink');
    }

    const response = await new Proxy().stockProductChangeSummary(this.subscriptionTemplateId, this.uniqueSubscriptionId, this.newSubscriptionTemplateId);

    if (response.head.fault !== null) throw new Error(response.head.fault);

    Object.keys(this.flags).forEach(key => {
      this.flags[key] = response.data[key];
    });

    this.newUniqueSubscriptionId = response.data.newUniqueSubscriptionId;
    this.footnotes = new Footnotes();
    this.oldProduct = Transformer.fetch(response.data.oldProduct, this.footnotes);
    this.newProduct = Transformer.fetch(response.data.newProduct, this.footnotes);
    emitter.emit('setBannerPicture', this.newProduct.images.headerImageChangeSummaryResourcePath);
    emitter.emit('setBannerHeadline', this.newProduct.texts.headerTitleProductChangeSummary);
    emitter.emit('setBannerSubheadline', this.newProduct.texts.headerSubTitleProductChangeSummary);

    this.$webtrekk.viewPageWithPayload('produktwechseldetails', {
      page_type: 'basket',
      shop_product_sku: this.newSubscriptionTemplateId,
      shop_product_status: 'add',
      shop_product_name: this.newProduct.texts?.productName,
      shop_product_quantity: 1,
      shop_product_price: this.newProduct.price?.value,
    });
  },

  methods: {
    async onSubmit(values) {
      this.orderInProgress = true;
      const params = {
        orderId: this.flags.orderId,
        subscriptionTemplateId: this.subscriptionTemplateId,
        newSubscriptionTemplateId: this.newSubscriptionTemplateId,
        uniqueSubscriptionId: this.uniqueSubscriptionId,
        acceptTerms: values.terms_and_conditions !== undefined ? values.terms_and_conditions : true,
        acceptRescissionRights: values.conditions !== undefined ? values.conditions : true,
        customerEmail: values.email,
      };

      const response = await new ChangeActionProxy().create(params);
      this.$router.push({
        name: STOCK_PRODUCT_CHANGE_CONFIRMATION,
        params: {
          acceptTerms: values.terms_and_conditions !== undefined ? values.terms_and_conditions : true,
          customerEmail: values.email,
          optin: this.isOptin ? 'optin' : null,
          newSubscriptionTemplateId: this.newSubscriptionTemplateId,
          confirmationInProgress: response?.data?.confirmationInProgress ? 'inProgress' : null,
          newUniqueSubscriptionId: response.data.newUniqueSubscriptionId,
          hideSelection: this.hideSelection,
        },
      });
    },

    onAbort() {
      if (this.flags.subscriptionBookedByExtern) {
        // = DescElemToBeSetToFinalize
        if (this.hasAccessStockOverview) {
          this.$router.push({ name: STOCK });
        } else {
          this.$router.push({ name: ERROR, params: { code: 'CANCEL_TARGET_DIALOGUE_NOT_ALLOWED' } });
        }
      } else if (this.hasAccessCatalogue) {
        this.$router.push({ name: PRODUCT_CATALOGUE });
      } else {
        this.$router.push({ name: ERROR, params: { code: 'CANCEL_TARGET_DIALOGUE_NOT_ALLOWED' } });
      }
    },
    ...mapActions('order', ['parseSubscriptionBookResponse']),
  },
};
</script>

<style scoped>
.button-bar > span {
  position: relative;
  top: 1px;
  display: inline-block;
}
</style>
